/**
 * HIPAA-Compliant Authentication Service
 * 
 * Features:
 * - Axios HTTP client with security interceptors
 * - Automatic JWT token injection (Authorization header)
 * - Automatic CSRF token injection for state-changing operations
 * - Automatic token refresh on 401 errors
 * - Request queueing during token refresh
 * - Secure error handling
 * 
 * Based on HIPAA_COMPLIANCE_COMPLETE_SECURITY_GUIDE.md
 * Frontend guide lines 12-247
 */

import axios from 'axios';
import { logError } from '../utils/errorLogger.js';

// API base URL - uses environment variable or defaults to root (no /api prefix)
// The vite proxy handles routing to backend based on path
const API_URL = import.meta.env.VITE_API_URL || '';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
});

// ========================================================================
// REQUEST INTERCEPTOR: Automatic Token & CSRF Injection
// ========================================================================

api.interceptors.request.use(
  (config) => {
    console.log('🔍 [REQUEST INTERCEPTOR] Called for:', config.url);
    
    // 1. Add JWT Bearer Token (for ALL requests)
    const accessToken = localStorage.getItem('accessToken');
    console.log('   📝 Access token in localStorage:', accessToken ? 'EXISTS (length: ' + accessToken.length + ')' : '❌ MISSING');
    
    if (accessToken && accessToken !== 'null' && accessToken !== 'undefined') {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
      console.log('   ✅ Authorization header ADDED');
    } else {
      console.log('   ⚠️  Authorization header NOT added (no token)');
    }

    // 2. Add CSRF Token for state-changing methods
    const method = config.method?.toLowerCase();
    console.log('   🔍 Request method:', method);
    
    if (['post', 'put', 'delete', 'patch'].includes(method)) {
      console.log('   🔍 State-changing method detected, adding CSRF...');
      const csrfToken = localStorage.getItem('csrfToken');
      console.log('   📝 CSRF token in localStorage:', csrfToken ? 'EXISTS (length: ' + csrfToken.length + ')' : '❌ MISSING');
      
      if (csrfToken && csrfToken !== 'null' && csrfToken !== 'undefined') {
        config.headers['X-CSRF-Token'] = csrfToken;
        console.log('   ✅ CSRF header ADDED');
      } else {
        console.log('   ⚠️  CSRF header NOT added (no token in localStorage)');
      }
    } else {
      console.log('   ℹ️  Read-only request (GET), CSRF not needed');
    }

    console.log('   📤 Final headers:', {
      Authorization: config.headers['Authorization'] ? 'Bearer ***' : 'MISSING',
      'X-CSRF-Token': config.headers['X-CSRF-Token'] ? '***' : 'none'
    });

    return config;
  },
  (error) => {
    console.error('❌ Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// ========================================================================
// RESPONSE INTERCEPTOR: Automatic Token Refresh
// ========================================================================

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => {
    return response; // Success - pass through
  },
  async (error) => {
    const originalRequest = error.config;

    // Log all intercepted errors for debugging
    logError('RESPONSE_INTERCEPTOR', `Intercepted error: ${error.response?.status || 'NO_STATUS'}`, {
      url: originalRequest.url,
      status: error.response?.status,
      statusText: error.response?.statusText,
      hasResponse: !!error.response,
      message: error.message
    });

    // CRITICAL FIX: Don't intercept 401s from auth endpoints (login/register)
    // Those are legitimate auth failures, not token expiry
    const isAuthEndpoint = originalRequest.url?.includes('/auth/login') || 
                          originalRequest.url?.includes('/auth/register') ||
                          originalRequest.url?.includes('/auth/oauth');
    const isRefreshEndpoint = originalRequest.url?.includes('/auth/refresh');
    
    // If refresh endpoint itself returns 401, tokens are corrupted - clear and reload
    if (isRefreshEndpoint && error.response?.status === 401) {
      console.log('Refresh token endpoint returned 401 - tokens are invalid, clearing storage and reloading');
      logError('REFRESH_401', 'Refresh endpoint returned 401 - clearing storage', {
        url: originalRequest.url,
        status: error.response?.status
      });
      
      // Clear all tokens and reload page (same as user's manual fix)
      localStorage.clear();
      window.location.reload();
      
      return Promise.reject(error);
    }
    
    if (isAuthEndpoint) {
      // Let auth errors pass through to the calling code (Login.jsx)
      logError('AUTH_ENDPOINT', 'Auth endpoint error - passing through', { url: originalRequest.url });
      return Promise.reject(error);
    }

    // Only handle 401 errors for authenticated API calls and avoid infinite loops
    if (error.response?.status === 401 && !originalRequest._retry) {
      logError('TOKEN_REFRESH', 'Got 401 - attempting token refresh', { url: originalRequest.url });
      
      // SCENARIO A: Already refreshing - queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return api(originalRequest); // Retry with new token
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      // SCENARIO B: First 401 - initiate refresh
      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refreshToken');
      
      if (!refreshToken || refreshToken === 'null') {
        // No refresh token - force logout
        console.log('No refresh token available, redirecting to session expiry');
        logError('FORCED_LOGOUT', 'No refresh token - forcing logout', {
          hasRefreshToken: !!refreshToken,
          refreshTokenValue: refreshToken
        });
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('csrfToken');
        window.location.href = '/hipaa-session-expiry';
        isRefreshing = false;
        return Promise.reject(error);
      }

      try {
        console.log('Attempting to refresh token...');
        
        // Call refresh endpoint
        const response = await api.post('/auth/refresh', { refreshToken });
        
        const { accessToken, refreshToken: newRefreshToken, csrfToken } = response.data;
        
        // Update stored tokens
        localStorage.setItem('accessToken', accessToken);
        if (newRefreshToken) {
          localStorage.setItem('refreshToken', newRefreshToken);
        }
        if (csrfToken) {
          localStorage.setItem('csrfToken', csrfToken);
        }
        
        // Update original request with new token
        originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;
        
        // Process all queued requests with new token
        processQueue(null, accessToken);
        
        console.log('Token refreshed successfully');
        isRefreshing = false;
        
        // Retry original request with new token
        return api(originalRequest);
        
      } catch (refreshError) {
        console.log('Token refresh failed');
        
        // Check if refresh endpoint itself returned 401 (corrupted tokens)
        const isRefresh401 = refreshError.response?.status === 401 && 
                            refreshError.config?.url?.includes('/auth/refresh');
        
        if (isRefresh401) {
          // Refresh token is invalid - clear everything and reload (same as user's manual fix)
          console.log('Refresh token invalid (401) - clearing storage and reloading page');
          logError('REFRESH_TOKEN_INVALID', 'Refresh endpoint returned 401 - clearing storage and reloading', {
            status: refreshError.response?.status,
            url: refreshError.config?.url
          });
          
          // Clear all tokens and reload (automatic fix for endless loading)
          localStorage.clear();
          window.location.reload();
          
          // Don't continue processing - page is reloading
          return Promise.reject(refreshError);
        }
        
        // Other refresh errors - clear tokens and redirect to session expiry
        console.log('Token refresh failed, redirecting to session expiry');
        
        // Clear all tokens
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('csrfToken');
        
        // Reject all queued requests
        processQueue(refreshError);
        
        isRefreshing = false;
        
        // Redirect to HIPAA session expiry page
        window.location.href = '/hipaa-session-expiry';
        
        return Promise.reject(error);
      }
    }

    // Not a 401 error - pass through
    return Promise.reject(error);
  }
);

// ========================================================================
// AUTHENTICATION API FUNCTIONS
// ========================================================================

/**
 * Login with email and password
 */
export const login = async (email, password) => {
  try {
    const response = await api.post('/auth/login', { email, password });
    
    // Tokens are nested under response.data.tokens
    const tokens = response.data.tokens || {};
    
    console.log('✅ Login response received:', {
      hasAccessToken: !!tokens.accessToken,
      hasRefreshToken: !!tokens.refreshToken,
      hasCsrfToken: !!tokens.csrfToken,
      hasUser: !!response.data.user
    });
    
    // Store tokens from nested tokens object
    if (tokens.accessToken) {
      localStorage.setItem('accessToken', tokens.accessToken);
      localStorage.setItem('refreshToken', tokens.refreshToken);
      localStorage.setItem('csrfToken', tokens.csrfToken);
      console.log('✅ Tokens stored in localStorage');
    } else {
      console.error('❌ No accessToken in response!');
    }
    
    // Return in expected format for AuthContext AND Login component
    return {
      success: true,
      user: response.data.user,
      tokens: {
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        csrfToken: tokens.csrfToken
      }
    };
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

/**
 * Register new user
 */
export const register = async (name, email, password) => {
  try {
    const response = await api.post('/auth/register', { name, email, password });
    
    // Tokens are nested under response.data.tokens
    const tokens = response.data.tokens || {};
    
    console.log('✅ Registration response received:', {
      hasAccessToken: !!tokens.accessToken,
      hasUser: !!response.data.user
    });
    
    // Store tokens if returned
    if (tokens.accessToken) {
      localStorage.setItem('accessToken', tokens.accessToken);
      localStorage.setItem('refreshToken', tokens.refreshToken);
      localStorage.setItem('csrfToken', tokens.csrfToken);
      console.log('✅ Tokens stored after registration');
    }
    
    return response.data;
  } catch (error) {
    console.error('Register error:', error);
    throw error;
  }
};

/**
 * Logout (invalidate session)
 */
export const logout = async () => {
  try {
    const refreshToken = localStorage.getItem('refreshToken');
    
    if (refreshToken) {
      await api.post('/auth/logout', { refreshToken });
    }
  } catch (error) {
    console.warn('Logout API call failed:', error);
  } finally {
    // Clear localStorage regardless of API success
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('csrfToken');
  }
};

/**
 * Verify current token
 */
export const verifyToken = async () => {
  try {
    const response = await api.get('/auth/verify');
    return response.data;
  } catch (error) {
    console.error('Token verification failed:', error);
    throw error;
  }
};

/**
 * Get current user profile
 */
export const getCurrentUser = async () => {
  try {
    const response = await api.get('/auth/verify');
    return response.data.user;
  } catch (error) {
    console.error('Get current user failed:', error);
    return null;
  }
};

/**
 * Process OAuth callback
 */
export const processOAuthCallback = async (provider, code, state) => {
  try {
    const response = await api.post('/auth/oauth/callback', {
      provider,
      code,
      state
    });
    
    // Store tokens
    if (response.data.tokens) {
      localStorage.setItem('accessToken', response.data.tokens.accessToken);
      localStorage.setItem('refreshToken', response.data.tokens.refreshToken);
      localStorage.setItem('csrfToken', response.data.tokens.csrfToken);
    }
    
    return response.data;
  } catch (error) {
    console.error('OAuth callback error:', error);
    throw error;
  }
};

// Export the configured axios instance as default
// This allows components to make requests without manually adding tokens
// Example: authService.get('/endpoint') or authService.post('/endpoint', data)
export default api;

